/**
 * RTWO Asset & Token Engine — Internal Event Bus
 */

export const ASSTInternalEventTypes = {
  ASSET_REGISTERED: 'asset_registered',
  SUPPLY_POLICY_CONFIGURED: 'supply_policy_configured',
  PERMISSION_GRANTED: 'permission_granted',
  TRANSFER_RULE_CONFIGURED: 'transfer_rule_configured',
  METADATA_CONFIGURED: 'metadata_configured',
  ASSET_EVENT_PUBLISHED: 'asset_event_published',
  LIFECYCLE_TRANSITIONED: 'lifecycle_transitioned',
  ASSET_VALIDATED: 'asset_validated',
} as const;

export type ASSTInternalEventType =
  (typeof ASSTInternalEventTypes)[keyof typeof ASSTInternalEventTypes];

export interface ASSTInternalEvent<T = unknown> {
  id: string;
  type: ASSTInternalEventType;
  moduleId: 'RTWO-ASST-001';
  payload: T;
  timestamp: number | null;
}

export type ASSTInternalEventHandler<T = unknown> = (event: ASSTInternalEvent<T>) => void;

class ASSTInternalEventBus {
  private subscriptions = new Map<ASSTInternalEventType, ASSTInternalEventHandler[]>();

  subscribe<T>(eventType: ASSTInternalEventType, handler: ASSTInternalEventHandler<T>): void {
    const handlers = this.subscriptions.get(eventType) ?? [];
    handlers.push(handler as ASSTInternalEventHandler);
    this.subscriptions.set(eventType, handlers);
  }

  unsubscribe<T>(eventType: ASSTInternalEventType, handler: ASSTInternalEventHandler<T>): void {
    const handlers = this.subscriptions.get(eventType) ?? [];
    this.subscriptions.set(
      eventType,
      handlers.filter((entry) => entry !== handler)
    );
  }

  emit<T>(eventType: ASSTInternalEventType, payload: T): string {
    const event: ASSTInternalEvent<T> = {
      id: `asst_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      type: eventType,
      moduleId: 'RTWO-ASST-001',
      payload,
      timestamp: null,
    };

    const handlers = this.subscriptions.get(eventType) ?? [];
    for (const handler of handlers) {
      try {
        handler(event);
      } catch (error) {
        console.error(`[ASSTInternalEventBus] Handler error for ${eventType}:`, error);
      }
    }

    return event.id;
  }
}

export const assInternalEventBus = new ASSTInternalEventBus();
